import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, Clock, User, Tag, PenSquare } from "lucide-react";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { fetchBlogArticle } from "../lib/api";

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchBlogArticle(slug)
      .then((d) => setArticle(d.article))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8">
        <div className="h-10 w-2/3 rounded shimmer" />
        <div className="mt-6 space-y-3">
          <div className="h-4 rounded shimmer" />
          <div className="h-4 w-5/6 rounded shimmer" />
          <div className="h-4 w-4/6 rounded shimmer" />
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center sm:px-8">
        <SEO title="Article introuvable" noindex />
        <h1 className="font-heading text-3xl font-bold text-slate-50">Article introuvable</h1>
        <p className="mt-3 text-slate-400">
          L'article que tu cherches n'existe pas ou n'est plus publié.
        </p>
        <Button onClick={() => navigate("/blog")} className="mt-6 bg-cyan-500 text-slate-950">
          Retour au blog
        </Button>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Person", name: article.author_name },
    publisher: {
      "@type": "Organization",
      name: "Autopièce Malin",
      logo: { "@type": "ImageObject", url: `${origin}/og-image.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/blog/${article.slug}` },
    image: article.cover_image_url || `${origin}/og-image.png`,
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        type="article"
        image={article.cover_image_url}
        publishedTime={article.published_at}
        modifiedTime={article.updated_at}
        author={article.author_name}
        jsonLd={jsonLd}
      />
      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-8">
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-cyan-300"
        >
          <ArrowLeft className="h-3 w-3" /> Retour au blog
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-cyan-500/40 bg-cyan-500/10 text-[10px] uppercase tracking-wider text-cyan-300"
          >
            {article.category}
          </Badge>
          {article.author_type === "visitor" && (
            <Badge
              variant="outline"
              className="border-amber-500/40 bg-amber-500/10 text-[10px] uppercase tracking-wider text-amber-300"
            >
              Contribution communauté
            </Badge>
          )}
        </div>

        <h1
          data-testid="blog-article-title"
          className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl"
        >
          {article.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            Par <span className="text-slate-300">{article.author_name}</span>
          </span>
          {article.published_at && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(article.published_at).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {article.reading_time_min} min de lecture
          </span>
        </div>

        {article.cover_image_url && (
          <img
            src={article.cover_image_url}
            alt={article.title}
            className="mt-8 w-full rounded-2xl border border-slate-800 object-cover"
          />
        )}

        <div
          data-testid="blog-article-content"
          className="prose prose-invert prose-cyan mt-10 max-w-none prose-headings:font-heading prose-headings:text-slate-50 prose-p:text-slate-300 prose-strong:text-slate-100 prose-a:text-cyan-300 prose-li:text-slate-300 prose-code:text-amber-300"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
        </div>

        {article.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-slate-800 pt-6">
            <Tag className="h-3 w-3 text-slate-500" />
            {article.tags.map((t) => (
              <Badge
                key={t}
                variant="outline"
                className="border-slate-700 bg-slate-900/50 text-[10px] text-slate-400"
              >
                #{t}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-14 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-center">
          <h2 className="font-heading text-xl font-bold text-slate-50">
            Tu as une astuce ou un retour d'atelier à partager ?
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Propose ton article à la communauté : il sera relu puis publié ici.
          </p>
          <Link to="/blog/soumettre" data-testid="blog-article-cta-contribute">
            <Button className="mt-5 gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              <PenSquare className="h-4 w-4" />
              Soumettre mon article
            </Button>
          </Link>
        </div>
      </article>
    </>
  );
}
